<script setup lang="ts">
  import { Select, SelectOption, Tooltip } from 'ant-design-vue'
  import { ExclamationCircleOutlined, FileSearchOutlined } from '@ant-design/icons-vue'
  import { watch, ref, computed } from 'vue'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { PlotDataSourceType } from '../models/PlotDataSourceType'
  import { toast } from 'vue-sonner'
  import router from '@/router'
  import DropdownMenu from '@/components/ui/dropdown-menu/DropdownMenu.vue'
  import DropdownMenuTrigger from '@/components/ui/dropdown-menu/DropdownMenuTrigger.vue'
  import DropdownMenuContent from '@/components/ui/dropdown-menu/DropdownMenuContent.vue'
  import DropdownMenuItem from '@/components/ui/dropdown-menu/DropdownMenuItem.vue'
  import { ExternalLink } from 'lucide-vue-next'

  interface IPlotDataSourceSelectConfigurationProps {
    availableDataSources: Array<{
      value: string
      label: string
      id: string
      type: PlotDataSourceType
    }>
    dataSourceTypes: Array<PlotDataSourceType>
    modelValue: string
    placeHolder?: string
  }

  const props = defineProps<IPlotDataSourceSelectConfigurationProps>()

  const emit = defineEmits<{
    'update:modelValue': [value: string]
    'data-source-selected': [value: PlotDataSourceType]
  }>()

  const selectedValue = ref<string>(props.modelValue)
  const dataSourceTypes = ref<Set<PlotDataSourceType>>(new Set(props.dataSourceTypes ?? []))
  const hasInvalidSelection = ref<boolean>(false)

  const availableDataSources = computed(() =>
    (props.availableDataSources ?? []).filter((x) => dataSourceTypes.value.has(x.type))
  )

  watch(
    () => props.modelValue,
    (newVal) => {
      selectedValue.value = newVal
    }
  )

  watch(selectedValue, () => {
    emit('update:modelValue', selectedValue.value)
  })

  watch(
    selectedValue,
    () => {
      const hasNoSelection = !selectedValue.value?.length
      if (hasNoSelection) {
        hasInvalidSelection.value = true
        return
      }

      // Don't invalidate if sources haven't loaded yet
      if (!availableDataSources.value.length) return

      const match = availableDataSources.value.find((x) => x.value == selectedValue.value)
      if (match == null) {
        selectedValue.value = ''
        hasInvalidSelection.value = true
        return
      }
      hasInvalidSelection.value = false
      emit('data-source-selected', match.type)
    },
    { immediate: true }
  )

  watch(availableDataSources, () => {
    if (!availableDataSources.value.length || !selectedValue.value?.length) return
    const match = availableDataSources.value.find((x) => x.value == selectedValue.value)
    if (match) {
      hasInvalidSelection.value = false
      emit('data-source-selected', match.type)
    }
  })

  const { controlSize } = useWindowSize()

  const viewData = async (aplosFileId: string) => {
    if (!aplosFileId) toast.error('Error viewing data for selected file. Missing Id!')

    const resolved = router.resolve({
      path: '/app/data-viewer',
      query: { fileId: aplosFileId }
    })
    const url = window.location.origin + resolved.href
    window.open(url, '_blank', 'noopener,noreferrer')
  }
</script>

<template>
  <div class="relative w-full">
    <Select
      v-model:value="selectedValue"
      :placeholder="props.placeHolder"
      :size="controlSize"
      :status="hasInvalidSelection ? 'error' : ''"
      class="w-full"
      showSearch
      allowClear
    >
      <SelectOption
        v-for="option in availableDataSources"
        :key="option.value"
        :value="option.value"
      >
        {{ option.label }}
      </SelectOption>
      <template #suffixIcon>
        <span class="flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <div
                class="group relative inline-flex pr-[3px] opacity-50 transition hover:opacity-100"
                :class="{ 'mr-4': !hasInvalidSelection }"
              >
                <Tooltip placement="top" title="Preview source data">
                  <FileSearchOutlined
                    class="cursor-pointer text-foreground"
                    :style="{
                      fontSize: '.9rem',
                      opacity: !availableDataSources?.length ? 0.4 : 0.9
                    }"
                  />
                </Tooltip>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" class="min-w-[200px]">
              <DropdownMenuItem
                v-for="datasource in availableDataSources"
                :key="datasource.id"
                @click="() => viewData(datasource.id)"
                class="gap-2"
              >
                <ExternalLink class="size-4" />
                <span>{{ datasource.value ?? 'Source Name' }}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Tooltip
            v-if="hasInvalidSelection"
            title="Missing selection or specified data source is not available"
            placement="top"
          >
            <ExclamationCircleOutlined style="color: red; margin-top: 1px" />
          </Tooltip>
        </span>
      </template>
    </Select>
  </div>
</template>
